import type * as faceapi from 'face-api.js';

export type HeadPoseDir = 'left' | 'right' | 'up' | 'down' | 'front' | 'unknown';

export type HeadPoseEstimate = {
  yaw: number;
  pitch: number;
  direction: HeadPoseDir;
};

function avg(points: Array<{ x: number; y: number }>) {
  const n = points.length || 1;
  let x = 0;
  let y = 0;
  for (const p of points) {
    x += p.x;
    y += p.y;
  }
  return { x: x / n, y: y / n };
}

/**
 * Head pose from 68 landmarks in camera-buffer coords (not CSS-mirrored).
 *
 * Front-camera buffer (unmirrored): when the subject turns to THEIR left,
 * the nose moves toward the RIGHT side of the frame → yaw > 0 → 'left'.
 * That matches mirrored selfie UX ("왼쪽을 보세요" = turn your head left)
 * without flipping again in the UI layer.
 *
 * yaw > 0 → looking left (subject)
 * yaw < 0 → looking right
 * pitch > 0 → looking down
 * pitch < 0 → looking up
 */
export function estimateHeadPose(
  landmarks: faceapi.FaceLandmarks68,
): HeadPoseEstimate {
  const nose = landmarks.getNose();
  const jaw = landmarks.getJawOutline();
  const leftEye = avg(landmarks.getLeftEye());
  const rightEye = avg(landmarks.getRightEye());
  const eyesMid = {
    x: (leftEye.x + rightEye.x) / 2,
    y: (leftEye.y + rightEye.y) / 2,
  };
  const noseTip = nose[3] ?? nose[Math.floor(nose.length / 2)];

  // jaw[0] = image-left cheek, jaw[16] = image-right cheek (face-api 68)
  const cheekL = jaw[0];
  const cheekR = jaw[16];
  const faceW = Math.max(24, Math.hypot(cheekR.x - cheekL.x, cheekR.y - cheekL.y));

  // Nose offset from eye midpoint (primary)
  const yawFromEyes = (noseTip.x - eyesMid.x) / faceW;

  // Nose position between cheeks: 0 = left cheek, 1 = right cheek, 0.5 = center
  // > 0.5 → nose toward image-right → subject looking left
  const cheekSpan = Math.max(12, cheekR.x - cheekL.x);
  const yawFromCheeks = (noseTip.x - cheekL.x) / cheekSpan - 0.5;

  // Blend; cheeks help when face is rotated
  const yaw = yawFromEyes * 0.45 + yawFromCheeks * 0.55;
  const pitch = (noseTip.y - eyesMid.y) / faceW - 0.35;

  const YAW_T = 0.06;
  const PITCH_T = 0.06;
  const FRONT_YAW = 0.045;
  const FRONT_PITCH = 0.05;

  let direction: HeadPoseDir = 'front';
  if (Math.abs(yaw) < FRONT_YAW && Math.abs(pitch) < FRONT_PITCH) {
    direction = 'front';
  } else if (Math.abs(yaw) >= Math.abs(pitch)) {
    if (yaw > YAW_T) direction = 'left';
    else if (yaw < -YAW_T) direction = 'right';
    else direction = 'unknown';
  } else if (pitch < -PITCH_T) {
    direction = 'up';
  } else if (pitch > PITCH_T) {
    direction = 'down';
  } else {
    direction = 'unknown';
  }

  return { yaw, pitch, direction };
}

export function poseMatchesTarget(
  estimated: HeadPoseDir,
  target: Exclude<HeadPoseDir, 'unknown'>,
): boolean {
  return estimated === target;
}
